import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect, Suspense, lazy } from 'react'
import { Helmet } from 'react-helmet-async'
import { ThemeProvider } from './contexts/ThemeContext'
import Header from './components/Header'
import Footer from './components/Footer'
import Home from './pages/Home'
// Lazy-loaded routes to reduce initial bundle size
const HowItWorks = lazy(() => import('./pages/HowItWorks'))
const Pricing = lazy(() => import('./pages/Pricing'))
const FAQ = lazy(() => import('./pages/FAQ'))
const About = lazy(() => import('./pages/About'))
const PropertyManagers = lazy(() => import('./pages/PropertyManagers'))
const VerificationStatus = lazy(() => import('./pages/VerificationStatus'))
const Careers = lazy(() => import('./pages/Careers'))
const CareerDetail = lazy(() => import('./pages/CareerDetail'))
const Atlanta = lazy(() => import('./pages/locations/Atlanta'))
const Dallas = lazy(() => import('./pages/locations/Dallas'))
const Locations = lazy(() => import('./pages/Locations'))
const Verification = lazy(() => import('./pages/Verification'))
const VerificationSuccess = lazy(() => import('./pages/VerificationSuccess'))
import { trackPageView, initializeScrollTracking, initializeEngagementTracking, initializeWebVitals } from './utils/analytics.js'
import './App.css'

// Analytics tracking component
function AnalyticsTracker() {
  const location = useLocation()

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname, document.title)

    // Initialize tracking for new page
    const cleanupScroll = initializeScrollTracking()
    const cleanupEngagement = initializeEngagementTracking()
    // Initialize field Web Vitals (mobile only) once per page
    const cleanupWebVitals = initializeWebVitals({ mobileOnly: true })

    // Cleanup when component unmounts or route changes
    return () => {
      if (cleanupScroll) cleanupScroll()
      if (cleanupEngagement) cleanupEngagement()
      if (cleanupWebVitals) cleanupWebVitals()
    }
  }, [location])

  return null
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    // Ensure new pages start at the top
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [pathname])

  return null
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <div className="min-h-screen bg-background">
          <AnalyticsTracker />
          <ScrollToTop />
          {/* Skip to main content for keyboard users */}
          <a href="#main-content" className="skip-link">Skip to content</a>
          <Header />
          <main id="main-content" role="main" tabIndex={-1}>
            <Suspense fallback={<div className="py-20 text-center text-gray-700">Loading…</div>}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/careers/:slug" element={<CareerDetail />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/landlords" element={<Navigate to="/property-managers" replace />} />
                <Route path="/locations" element={<Locations />} />
                <Route path="/locations/atlanta" element={<Atlanta />} />
                <Route path="/locations/dallas" element={<Dallas />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/property-managers" element={<PropertyManagers />} />
                <Route path="/verification" element={<Verification />} />
                <Route path="/verification-success" element={<VerificationSuccess />} />
                <Route path="/verify/:code" element={<VerificationStatus />} />
                <Route path="/resources" element={
                  <>
                    <Helmet>
                      <title>ESA Resources | Authentipet</title>
                      <meta name="description" content="Educational resources about Emotional Support Animals and the ESA letter verification process." />
                      <link rel="canonical" href="https://authentipets.com/resources" />
                    </Helmet>
                    <div className="container mx-auto px-4 py-20">
                      <h1 className="text-4xl font-bold mb-8">ESA Resources</h1>
                      <p className="text-lg">Educational resources about Emotional Support Animals and the verification process.</p>
                    </div>
                  </>
                } />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
      </Router>
    </ThemeProvider>
  )
}

export default App
